import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TriggerGroupType } from 'src/app/form-template/form-template-model';
import { AutopopulateService } from 'src/app/shared/autopopulate/autopopulate.service';
import {
  SuggestionConfig,
  WorkflowTrigger,
  WorkflowTriggerGroup
} from '../models/workflow-assistant.model';
import {
  operationDataSourceKeyToOperationTrackerMap,
  workflowAssistantSuggestionsConfig
} from '../schemas/workflow-assistant.schema';
import { ComparisonType } from './../models/workflow-assistant.model';

@Injectable({
  providedIn: 'root'
})
export class WorkflowAssistantService implements OnDestroy {
  private routerSubscription: Subscription;
  private readonly operationDataSourceKeyToOperationTrackerMap = operationDataSourceKeyToOperationTrackerMap;
  public suggestionsDisplay: SuggestionConfig[] = [];
  public expandedSuggestionIndex = -1;

  public showSuggestionsDisplay = false;
  public activeKey: string = '';

  public data: any = {};

  constructor(private router: Router, private autopopulateService: AutopopulateService) {
    // this.subscribeToRouterEvents();
  }

  // Not currently used but maybe for workflow assistant
  private subscribeToRouterEvents(): void {
    this.routerSubscription = this.router.events
      .pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const currentUrl = event.url; // Get the current URL
        // console.log('Current URL:', currentUrl);

        // Perform actions based on the route
      });
  }

  // Should be called on route change via resolver
  public updateData(key: string, $data: Observable<any>, dataKeysToKeep?: string[]) {
    this.activeKey = key;
    $data.subscribe(val => {
      if (dataKeysToKeep) {
        Object.keys(this.data).forEach(dataKey => {
          if (!dataKeysToKeep.includes(dataKey)) {
            delete this.data[dataKey];
          }
        });
        // this.data = { ...Object.keys(this.data).filter(key => dataKeysToKeep.includes(key)) };
      }

      this.data[key] = val;
      this.loadSuggestionsDisplay(key);
      this.expandedSuggestionIndex = -1;
    });
  }

  public loadSuggestionsDisplay(key: string) {
    this.suggestionsDisplay = [];
    this.generateSuggestions(key);
  }

  // Currently disabled
  public toggleDisplayVisibility() {
    // this.showSuggestionsDisplay = !this.showSuggestionsDisplay;
    // this.expandedSuggestionIndex = -1;
  }

  public toggleSelectedSuggestion(index) {
    if (this.expandedSuggestionIndex === index) {
      this.expandedSuggestionIndex = -1;
      return;
    }
    this.expandedSuggestionIndex = index;
  }

  public generateSuggestions(key: string) {
    if (workflowAssistantSuggestionsConfig[key]) {
      for (let suggestionConfig of workflowAssistantSuggestionsConfig[key]) {
        if (suggestionConfig.triggerGroups) {
          const addToActiveDisplay = suggestionConfig.triggerGroups.some(triggerGroup =>
            this.evaluateTriggerGroup(triggerGroup, this.data)
          );
          if (addToActiveDisplay) {
            this.suggestionsDisplay.push(suggestionConfig);
            // console.log('Adding to active display', this.suggestionsDisplay);
          }
        }
      }
    }
  }

  public evaluateTriggerGroup(triggerGroup: WorkflowTriggerGroup, data: any) {
    return triggerGroup.triggers[triggerGroup.type === TriggerGroupType.or ? 'some' : 'every'](
      trigger => {
        const res = this.evaluateTrigger(trigger, data);
        // console.log(`Result: ${res}`);
        return res;
      }
    );
  }

  public evaluateTrigger(trigger: WorkflowTrigger, data: any) {
    const extractedValue = this.autopopulateService.getJsonDataViaDotNotation(
      trigger.dataPath,
      data
    );
    // console.log(
    //   `Operator: ${trigger.typeConfig.type}, Path: ${trigger.dataPath}, Extracted value: ${extractedValue}`
    // );
    if (extractedValue === undefined) {
      console.warn(`Undefined data at: ${trigger.dataPath}`);
      return false;
    }
    switch (trigger.typeConfig.type) {
      case ComparisonType.isNullOrEmpty: {
        if (extractedValue === null) {
          return true;
        }
        if (typeof extractedValue === 'string' && extractedValue === '') {
          return true;
        }
        if (Array.isArray(extractedValue) && extractedValue.length === 0) {
          return true;
        }
        return false;
      }
      case ComparisonType.notEmpty: {
        if (
          (typeof extractedValue === 'string' || Array.isArray(extractedValue)) &&
          extractedValue.length > 0
        ) {
          return true;
        }
        return false;
      }
    }
  }
  public onOperationIntercepted(operationName, operationData) {
    Object.keys(operationDataSourceKeyToOperationTrackerMap).forEach(key => {
      let operationTrackerConfigs = this.operationDataSourceKeyToOperationTrackerMap[key];
      if (operationTrackerConfigs) {
        operationTrackerConfigs.forEach(config => {
          if (config.operationName === operationName) {
            let incomingData = this.autopopulateService.getJsonDataViaDotNotation(
              config.extractDataFrom,
              operationData
            );
            if (this.data[key]) {
              const currentId = this.data[key].id;
              const incomingId = incomingData.id;
              // Update data if ids match
              if (currentId === incomingId) {
                this.data[key] = { ...this.data[key], ...(incomingData ? incomingData : {}) };
              } else {
                // For testing
                console.warn(
                  `Id mismatch. Current data: ${this.data[key]}, Incoming data: ${incomingData}`
                );
              }
            } else {
              // Update if data does not yet exist
              this.data[key] = { ...this.data[key], ...(incomingData ? incomingData : {}) };
            }

            // console.log(`${operationName}: INTERCEPTED AND UPDATED`, this.data);
          }
        });
      }
    });
    this.loadSuggestionsDisplay(this.activeKey);
  }

  public resetData(keys: string[]) {
    for (let key of keys) {
      if (key === '*') {
        this.data = {};
        break;
      }
      delete this.data[key];
    }
    // console.log('RESET', this.data);
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
